import { FullScreenLoader } from '@components/Fillers/FullScreenLoader'
import Header from '@components/ScreenHeader'
import { RouteWithSceneType, SceneTypes } from '@components/Tabs'
import { TabBar } from '@components/TabsUniversal/tab-bar'
import { NeedsListComposed } from '@features/needs/components/NeedsList.composed'
import { needKeysNext } from '@features/needs/model/query-keys'
import {
  useActionParamsFromAlias,
  useOrg,
} from '@features/organizations/hooks/useOrganizations'
import { useRoadblockAction } from '@features/roadblock/hooks/useRoadblock'
import { FontAwesomeIcon, findIconDefinition } from '@purposity/icons'
import * as Colors from '@themes/Colors'
import Fonts from '@themes/Fonts'
import React, { useCallback, useRef } from 'react'
import {
  Animated,
  Platform,
  Pressable,
  Modal as RNModal,
  ScrollView,
  StyleSheet,
  Text,
  TextStyle,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native'
import { SceneMap } from 'react-native-tab-view'
import { createParam } from 'solito'
import { OrgActionMenu as ActionMenu } from '../components/ActionMenu'
import { OrgHeader } from '../components/OrgHeader'

function CreateNeedButton({ isMobile }: { isMobile: boolean }) {
  const [modalVisible, setModalVisible] = React.useState(false)
  const [alias] = useParam('alias', { initial: '' })
  const actionParams = useActionParamsFromAlias(alias, true)
  const { callback: handleAgreement } = useRoadblockAction({
    trigger: () => {
      window.open(
        'https://docs.google.com/forms/d/e/1FAIpQLSdP8ZzHGyheSKKlUF2i9TDE7OUG4Kbe8GJyzGyyD4LgJCEtEg/viewform',
        '_blank'
      )
    },
    referral: {
      entity: 'org',
      method: 'follow',
      params: { orgId: actionParams?.id || 0 },
    },
  })

  const handleCompleteAgreement = () => {
    setModalVisible(false)
    handleAgreement()
  }

  const buttonStyles = {
    backgroundColor: Colors.white,
    height: isMobile ? 24 : 32,
    paddingHorizontal: isMobile ? 8 : 12,
    borderRadius: isMobile ? 12 : 16,
    minWidth: isMobile ? 'auto' : 90,
    marginRight: isMobile ? 4 : 16,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
  }

  const iconStyles = {
    width: isMobile ? 16 : 20,
    height: isMobile ? 16 : 20,
    borderRadius: isMobile ? 8 : 10,
    marginRight: isMobile ? 4 : 6,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    borderWidth: 1.5,
    borderColor: Colors.newPurple,
    backgroundColor: Colors.white,
  }

  const textStyles = {
    fontSize: isMobile ? 11 : 14,
    letterSpacing: 0.5,
    color: Colors.newPurple,
    fontWeight: '700' as const,
    textTransform: 'uppercase' as const,
  }

  return (
    <>
      <Pressable
        style={({ pressed, hovered }) => [
          buttonStyles,
          (pressed || hovered) && localStyles.createNeedButtonHovered,
        ]}
        onPress={() => setModalVisible(true)}
      >
        <View
          style={{
            flexDirection: 'row' as const,
            alignItems: 'center' as const,
          }}
        >
          <View style={iconStyles}>
            <FontAwesomeIcon
              icon={findIconDefinition({ prefix: 'fas', iconName: 'plus' })}
              color={Colors.newPurple}
              style={{
                width: isMobile ? 9 : 13,
                height: isMobile ? 9 : 13,
              }}
            />
          </View>
          <Text style={textStyles}>Submit Need</Text>
        </View>
      </Pressable>

      <RNModal
        animationType="fade"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={localStyles.modalOverlay}>
          <View style={localStyles.modalContent}>
            <Pressable
              style={localStyles.closeButton}
              onPress={() => setModalVisible(false)}
            >
              <FontAwesomeIcon
                icon={findIconDefinition({
                  prefix: 'fas',
                  iconName: 'times',
                })}
                color={Colors.black}
                style={{ width: 16, height: 16 }}
              />
            </Pressable>

            <Text style={localStyles.modalTitle}>Submit Needs</Text>
            <Text style={localStyles.modalSubheader}>
              Get approved to submit needs for your organization.
            </Text>

            <Pressable
              style={localStyles.modalButton}
              onPress={handleCompleteAgreement}
            >
              <Text style={localStyles.modalButtonText}>
                Complete Agreement
              </Text>
            </Pressable>

            <Pressable
              onPress={() => {
                window.open('https://admin.purposity.com', '_blank')
                setModalVisible(false)
              }}
            >
              <Text style={localStyles.modalLink}>
                Log in to submit needs here
              </Text>
            </Pressable>
          </View>
        </View>
      </RNModal>
    </>
  )
}

function HeaderRight(headerRightProps: {
  tintColor?: string
  aliasOrValias: string
}) {
  const { width } = useWindowDimensions()
  const isMobile = width <= 768

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <CreateNeedButton isMobile={isMobile} />
      <ActionMenu
        aliasOrValias={headerRightProps.aliasOrValias}
        color={headerRightProps.tintColor}
      />
    </View>
  )
}

const { useParam } = createParam<{ alias: string }>()

export function OrgDetailScreen() {
  const [aliasOrValias] = useParam('alias', {
    initial: '',
  })

  const screenQuery = useOrg(aliasOrValias)

  if (screenQuery.isLoading || screenQuery.isLoadingError || !aliasOrValias)
    return <FullScreenLoader />

  return (
    <View style={{ flex: 1, borderWidth: 1, borderColor: Colors.borderGray }}>
      <Header
        title="Organizations"
        backAction
        headerShown
        headerTintColor={Colors.black}
        headerTitleStyle={{
          ...Fonts.typeWithWeight.bold,
          fontSize: 16,
        }}
        headerShadowVisible={false}
        headerBackTitleVisible={false}
        headerRight={(props) => (
          <HeaderRight {...props} aliasOrValias={aliasOrValias} />
        )}
      />
      <ScrollView>
        <DetailView aliasOrValias={aliasOrValias} />
      </ScrollView>
    </View>
  )
}

function DetailView(props: { aliasOrValias: string }) {
  const RenderScene = SceneMap({
    open: () => (
      <NeedsListComposed
        options={
          needKeysNext.org({ aliasOrValias: props.aliasOrValias })._ctx.live
        }
      />
    ),
    met: () => (
      <NeedsListComposed
        options={
          needKeysNext.org({ aliasOrValias: props.aliasOrValias })._ctx.met
        }
      />
    ),
  })

  const [routes] = React.useState<RouteWithSceneType[]>([
    { key: 'open', title: 'Open Needs', sceneType: SceneTypes.FlatList },
    { key: 'met', title: 'Met Needs', sceneType: SceneTypes.FlatList },
  ])

  const [currentIndex, setIndexTo] = React.useState(0)

  const jumpTo = useCallback(
    (key: string) => setIndexTo(routes.findIndex((route) => route.key === key)),
    [routes]
  )
  const handleTabPress = useCallback(
    (nextIndex: number) => {
      if (Platform.OS === 'web') {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      }
      setIndexTo(nextIndex)
    },
    [setIndexTo]
  )
  const position = useRef(new Animated.Value(0)).current

  return (
    <>
      <OrgHeader aliasOrValias={props.aliasOrValias} />
      <TabBar {...{ routes, currentIndex, handleTabPress }} />
      <RenderScene
        route={routes[currentIndex]}
        jumpTo={jumpTo}
        position={position}
      />
    </>
  )
}

const localStyles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  } as ViewStyle,
  modalContent: {
    backgroundColor: Colors.white,
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 400,
    alignItems: 'center',
    position: 'relative',
  } as ViewStyle,
  closeButton: {
    position: 'absolute',
    right: 16,
    top: 16,
    padding: 8,
    backgroundColor: 'transparent',
    borderWidth: 0,
  } as ViewStyle,
  modalTitle: {
    fontSize: 24,
    fontWeight: '600',
    marginBottom: 8,
    color: Colors.black,
  } as TextStyle,
  modalSubheader: {
    fontSize: 16,
    color: Colors.textGray,
    textAlign: 'center',
    marginBottom: 24,
  } as TextStyle,
  modalButton: {
    backgroundColor: Colors.primary,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 24,
    width: '100%',
    alignItems: 'center',
    marginBottom: 16,
  } as ViewStyle,
  modalButtonText: {
    color: Colors.white,
    fontSize: 16,
    fontWeight: '600',
  } as TextStyle,
  modalLink: {
    color: Colors.primary,
    fontSize: 14,
    textDecorationLine: 'underline',
  } as TextStyle,
  createNeedButtonHovered: Platform.select({
    web: {
      opacity: 0.9,
      transform: [{ translateY: -1 }],
    },
    default: {
      opacity: 0.9,
    },
  }) as ViewStyle,
})
